import { Component, type ErrorInfo, type ReactNode } from "react";

import { Button } from "@/components/ui/button";

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback: (error: Error, reset: () => void) => ReactNode;
  onError?: (error: Error, info: ErrorInfo) => void;
}

interface ErrorBoundaryState {
  error: Error | null;
}

/**
 * Error boundary genérico (React só sabe pegar erro de render em classe).
 * Não use diretamente nas telas — use `AppErrorBoundary` (raiz do app) ou
 * `RouteErrorBoundary` (por rota/seção) abaixo, que já vêm com um fallback
 * pronto no visual do produto.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    this.props.onError?.(error, info);
  }

  reset = (): void => {
    this.setState({ error: null });
  };

  render(): ReactNode {
    if (this.state.error) {
      return this.props.fallback(this.state.error, this.reset);
    }
    return this.props.children;
  }
}

/**
 * Boundary de topo do app — só deve envolver a árvore inteira, uma vez, no
 * ponto de montagem (main.tsx / shell do Agent 2). Se algo escapar até aqui
 * é porque uma boundary de rota mais interna também falhou.
 */
export function AppErrorBoundary({ children }: { children: ReactNode }) {
  return (
    <ErrorBoundary
      onError={(error) => console.error("[AppErrorBoundary]", error)}
      fallback={(error, reset) => (
        <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-bg px-6 text-center">
          <h1 className="text-lg font-semibold text-fg">Algo deu errado.</h1>
          <p className="max-w-md text-sm text-fg-soft">
            A aplicação encontrou um erro inesperado e precisa recarregar.
            {import.meta.env.DEV ? ` (${error.message})` : ""}
          </p>
          <Button
            onClick={() => {
              reset();
              window.location.reload();
            }}
          >
            Recarregar
          </Button>
        </div>
      )}
    >
      {children}
    </ErrorBoundary>
  );
}

/**
 * Boundary de rota/seção — isola a falha de uma tela sem derrubar o shell
 * (sidebar/topbar) inteiro. O Agent 2 deve envolver cada rota com este
 * componente no router.
 */
export function RouteErrorBoundary({ children }: { children: ReactNode }) {
  return (
    <ErrorBoundary
      onError={(error) => console.error("[RouteErrorBoundary]", error)}
      fallback={(error, reset) => (
        <div className="flex flex-col items-center gap-2.5 rounded-lg border border-danger-weak bg-danger-weak/40 px-4 py-12 text-center">
          <h2 className="text-sm font-semibold text-fg">Não foi possível exibir esta página.</h2>
          <p className="max-w-md text-[0.8125rem] text-fg-soft">
            {import.meta.env.DEV ? error.message : "Tente novamente em instantes."}
          </p>
          <Button variant="secondary" size="sm" onClick={reset}>
            Tentar novamente
          </Button>
        </div>
      )}
    >
      {children}
    </ErrorBoundary>
  );
}
